// Shared tag-search matcher used by:
//   • SearchFilters.jsx filter-panel typeahead (narrows the visible
//     chip grid as the user types in the panel's tag search box)
//   • HeroSearchTypeahead.jsx hero-bar typeahead (suggests chips
//     under the main search input as the user types their query)
//
// Both surfaces match against the same haystack and the same scoring
// rules so the UX feels consistent. Single source of truth.
//
// Why local, not network: km_tags + practices total ~80 rows × ~10
// short strings each — well under 30 KB once cached by useTagOptions.
// Sub-millisecond scan, no debounce needed, no RPC pressure.

import { FILTER_DIMENSIONS } from '@/lib/filters';

// Lowercased haystack for one tag entry. Combines every text field a
// user might recall: display_name, sanskrit_name, description, the
// raw id, an underscore-normalised form of the id ("monkey_mind"
// → "monkey mind"), and the curated aliases array.
//
// km_tags rows (after migration 014/015/018) carry aliases.
// PRACTICES rows mirror tags.py and carry their own aliases. Either
// source plugs in here unchanged.
export function tagHaystack(tag) {
  const aliasJoined = Array.isArray(tag.aliases) ? tag.aliases.join(' ') : '';
  const idAsWords = (tag.id || '').replace(/_/g, ' ');
  return [
    tag.display_name,
    tag.sanskrit_name,
    tag.description,
    tag.id,
    idAsWords,
    aliasJoined,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

// Filler words / pronouns / connectives stripped before matching.
// Keep this list short — over-aggressive stopwording can swallow real
// tag terms. "feel"/"feeling"/"low"-style words came up in real user
// queries ("feeling low", "i feel stuck") so they earn their place.
const STOPWORDS = new Set([
  'a', 'an', 'the',
  'i', 'im', "i'm", 'me', 'my', 'we', 'us', 'our',
  'is', 'am', 'are', 'was', 'were', 'be', 'been', 'being',
  'feel', 'feeling', 'feels', 'felt',
  'have', 'has', 'had', 'do', 'does', 'did',
  'this', 'that', 'these', 'those', 'it',
  'and', 'or', 'but', 'with', 'of', 'in', 'on', 'at', 'to', 'for', 'from',
  'so', 'too', 'very', 'really', 'quite', 'bit', 'kinda', 'sorta',
  'just', 'still', 'always', 'never',
]);

// Split a query into significant tokens. Lowercased, stopwords removed,
// single-character tokens dropped (1-char tokens are too noisy for
// substring matching). Punctuation is treated as a separator.
function tokenize(normalisedQuery) {
  return normalisedQuery
    .split(/[\s,.;:!?'"()/\-]+/)
    .filter((t) => t.length >= 2 && !STOPWORDS.has(t));
}

// Minimum length before we allow the *reverse* prefix direction
// (query starts with field). Guards short fields like "art" or "ego"
// from swallowing long unrelated queries. The forward direction
// (field starts with query) has no guard — that's normal typeahead.
const STEM_MIN = 4;

// Bidirectional prefix (stem) match between a haystack field and the
// query token. Forward: the user typed a partial of the field
// ("medita" → "meditation"). Reverse: the user typed an inflected or
// longer form of a known term ("meaningful" → alias "meaning",
// "donating" → "donate") — length-guarded so it can't over-match.
function stemMatch(field, q) {
  if (!field) return false;
  if (field.startsWith(q)) return true;
  if (q.startsWith(field) && field.length >= STEM_MIN) return true;
  return false;
}

// Score a tag against the (lowercased, trimmed) query. Higher = better.
// We rank exact > prefix > substring across the haystack and against
// the aliases specifically. Aliases get a small boost so curated
// synonyms ("SCK", "burnout") win over accidental substring matches in
// long description text.
function scoreTag(tag, normalisedQuery) {
  if (!normalisedQuery) return 0;

  const haystack = tagHaystack(tag);
  const aliases = (tag.aliases || []).map((a) => a.toLowerCase());
  const id = (tag.id || '').toLowerCase();
  const displayName = (tag.display_name || '').toLowerCase();
  const sanskritName = (tag.sanskrit_name || '').toLowerCase();

  // Exact alias / id / display_name / sanskrit_name match wins outright.
  if (
    aliases.includes(normalisedQuery) ||
    id === normalisedQuery ||
    displayName === normalisedQuery ||
    sanskritName === normalisedQuery
  ) {
    return 100;
  }

  // Prefix (stem) match on any of the high-signal fields, both
  // directions — see stemMatch. Description is deliberately excluded
  // (it's matched only by the substring tier below) to keep stemming
  // from firing on incidental words in long descriptions.
  if (
    aliases.some((a) => stemMatch(a, normalisedQuery)) ||
    stemMatch(id, normalisedQuery) ||
    stemMatch(displayName, normalisedQuery) ||
    stemMatch(sanskritName, normalisedQuery)
  ) {
    return 60;
  }

  // Substring anywhere in the haystack — last-resort recall.
  if (haystack.includes(normalisedQuery)) {
    return 20;
  }

  return 0;
}

// Find the top tag matches across every filter dimension.
//
// Args:
//   query       — user input (raw, untrimmed is fine)
//   tagOptions  — the { [key]: [tag, ...] } map from useTagOptions()
//   options     — { limit?, minScore?, includeSelected?, selectedById? }
//
// Returns an array of { key, tag, score } sorted by score desc. `key`
// is the FILTER_DIMENSIONS key (e.g. 'practice_ids', 'obstacles') so
// callers can wire each suggestion straight into onToggleTag(key, id).
//
// Already-selected chips are filtered out by default (so the typeahead
// doesn't suggest something the user just added). Pass includeSelected
// to override.
//
// Matching strategy:
//   1. Score the whole query against each tag — catches exact phrase
//      matches like "monkey mind" → display_name (100).
//   2. Tokenize the query (drop filler/stopwords), score each token,
//      sum the per-token scores with a small boost when ≥2 tokens hit
//      the same tag — this is what makes "feeling low", "i feel
//      burned out", "really low motivation" surface the right chips
//      even though those exact strings appear nowhere in the data.
//   3. Take max(whole_query_score, token_sum). Whole-query exact
//      matches still win; token matching only adds recall.
//
// If the query is entirely stopwords (e.g. "i am"), tokenization
// returns nothing — we fall back to scoring the whole query so we
// still produce something.
export function findTagMatches(query, tagOptions, options = {}) {
  const {
    limit = 8,
    minScore = 1,
    includeSelected = false,
    selectedById = null, // { [key]: Set<id> } from filter state
  } = options;

  const normalisedQuery = (query || '').trim().toLowerCase();
  if (!normalisedQuery) return [];

  const tokens = tokenize(normalisedQuery);
  const hasTokens = tokens.length > 0;

  const matches = [];
  for (const { key } of FILTER_DIMENSIONS) {
    const dimensionTags = tagOptions[key] || [];
    for (const tag of dimensionTags) {
      if (
        !includeSelected &&
        selectedById?.[key] &&
        selectedById[key].has(tag.id)
      ) {
        continue;
      }

      const wholeScore = scoreTag(tag, normalisedQuery);

      let tokenSum = 0;
      let tokenHits = 0;
      if (hasTokens) {
        for (const t of tokens) {
          const s = scoreTag(tag, t);
          if (s > 0) {
            tokenSum += s;
            tokenHits += 1;
          }
        }
        // Multi-hit boost: a tag that matched ≥2 distinct meaningful
        // tokens is probably the right answer, lift it above tags that
        // only happened to substring-match a single token.
        if (tokenHits >= 2) tokenSum += 20;
      }

      const finalScore = Math.max(wholeScore, tokenSum);
      if (finalScore >= minScore) {
        matches.push({ key, tag, score: finalScore });
      }
    }
  }

  matches.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    // Stable secondary sort by display_name for predictable ordering.
    return (a.tag.display_name || '').localeCompare(b.tag.display_name || '');
  });

  return matches.slice(0, limit);
}

// Confidence floor for *implied* tag inference (query-side expansion).
// scoreTag tiers: exact = 100, stem = 60, substring = 20 (+20 multi-hit).
// We require >= 60 (stem-or-better) so only a confident synonym hit becomes
// an implied tag — a lone substring brush (20) is too noisy to feed back
// into search as a recall signal. The soft-lane contract is in migration 034.
const IMPLIED_MIN_SCORE = 60;

// Derive implied tag *slugs* from a free-text query, for query-side
// expansion (the RPC's implied_tag_ids param — migration 034). This reuses
// the very matcher the typeahead uses to suggest chips, so an alias the
// user typed but never clicked ("livelihood" → `career`) still widens
// recall. SOFT signal only: these never filter, they only boost / reorder.
//
// Already-selected chips are excluded (via selectedById): those are already
// expressed as hard filter_* params, so re-sending them as implied would be
// redundant. The implied lane is strictly for *inferred*, not chosen, tags.
//
// Returns a de-duplicated flat array of tag ids (slugs) — the form weight-A
// of search_vector indexes (migration 020) and the RPC normalises.
export function inferImpliedTagIds(query, tagOptions, options = {}) {
  const { limit = 6, selectedById = null } = options;
  const matches = findTagMatches(query, tagOptions, {
    limit,
    minScore: IMPLIED_MIN_SCORE,
    selectedById,
  });
  return Array.from(new Set(matches.map((m) => m.tag.id)));
}
